import { useLeadCreate } from "@gymflow/api";
import { cn } from "@gymflow/helpers";
import { LeadSourceDTO } from "@gymflow/types";
import { validatePhoneNumber } from "@gymflow/validation";
import { FormikProvider, useFormik } from "formik";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import z from "zod";
import { toFormikValidate, toFormikValidationSchema } from "zod-formik-adapter";

import { useClubSettings } from "../../providers";
import useGymflowModels from "../../store";
import { Button, FormInput } from "../atoms";
import { FormSelectLeadSource } from "../atoms/form/selects/components/FormSelectLeadSource";
import { FormPhoneNumberInput } from "../atoms/form/text/FormPhoneNumberInput";

const CreateLeadSchema = z.object({
  firstName: z.string().min(1, "common.required"),
  lastName: z.string().min(1, "common.required"),
  email: z.string().email(),
  mobileNumber: z
    .string()
    .trim()
    .min(1, "common.required")
    .refine((e) => !e || validatePhoneNumber(e), {
      message: `validation.phoneNumber`,
    }),
  source: z.object({
    id: z.number(),
    label: z.string(),
    value: z.custom<LeadSourceDTO>(),
  }),
});
export type SideBarLeadFormProps = {
  className?: string;
  hide: () => void;
  onLeadCreated: (newLeadId: number) => void;
  onChange: (newValue: any) => void;
  defaultValues: {
    firstName?: string;
    lastName?: string;
    mobileNumber?: string;
    email?: string;
    source?: { label: string; id: number; value: LeadSourceDTO };
  };
};

export function SideBarLeadForm({
  className,
  hide,
  onLeadCreated,
  onChange,
  defaultValues,
}: SideBarLeadFormProps) {
  const { t } = useTranslation();
  const {
     communicationProperties ,
  } = useClubSettings();
  const { api } = useGymflowModels();
  const createLeadMutation = useLeadCreate({ api });
  const formik = useFormik({
    initialValues: {
      firstName: defaultValues.firstName ?? "",
      lastName: defaultValues.lastName ?? "",
      mobileNumber: defaultValues.mobileNumber ?? "",
      email: defaultValues.email ?? "",
      source:
        defaultValues.source ??
        (undefined as unknown as z.infer<typeof CreateLeadSchema>["source"]),
    },
    validationSchema: toFormikValidationSchema(CreateLeadSchema),
    validate: toFormikValidate(CreateLeadSchema),
    onSubmit: async (values) => {
      const result = await createLeadMutation.mutateAsync({
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        mobileNumber: values.mobileNumber,
        sourceId: values.source.id,
        emailCommunication: communicationProperties.defaultEmailCommunication,
        smsCommunication: communicationProperties.defaultSmsCommunication,
      });
      onLeadCreated(result.id);
      hide();
    },
  });
  useEffect(() => {
    onChange({
      ...formik.values,
    });
  }, [formik.values, onChange]);

  return (
    <FormikProvider value={formik}>
      <div
        className={cn(
          "flex size-full flex-col  min-h-fit gap-4 justify-between",
          className,
        )}
      >
        <div className="flex flex-col gap-4">
          <FormInput
            name="email"
            label={t("page.newLeadSidebar.email")}
            placeholder={t("page.newLeadSidebar.email")}
            containerClassName="w-full"
          />
          <FormInput
            name="firstName"
            label={t("page.newLeadSidebar.firstName")}
            placeholder={t("page.newLeadSidebar.firstName")}
            containerClassName="w-full"
          />
          <FormInput
            name="lastName"
            label={t("page.newLeadSidebar.lastName")}
            placeholder={t("page.newLeadSidebar.lastName")}
            containerClassName="w-full"
          />
          <FormPhoneNumberInput
            name="mobileNumber"
            label={t("page.newLeadSidebar.mobileNumber")}
            placeholder={t("page.newLeadSidebar.mobileNumber")}
            className="w-full"
          />
          <FormSelectLeadSource name="source" />
        </div>

        <Button
          className="w-full"
          intent="secondary"
          onClick={async () => {
            await formik.submitForm();
          }}
        >
          {t("page.newLeadSideBar.createUser")}
        </Button>
      </div>
    </FormikProvider>
  );
}
